import type { Inline, LegalBlock, LegalContent } from "../content/legal";
import { Editable } from "../edit";
import { useSiteLink } from "../link";

// Matches the inline style the original GovernmentDataRequestPolicy page used
// for its ordered lists.
const OL_STYLE = { margin: 0, paddingLeft: 20, display: "grid", gap: 8 } as const;

// Every inline span carries a `.text` field, so each one's visible text is
// click-to-editable; link targets / emails keep their structure untouched.
function Inlines({ spans, path }: { spans: Inline[]; path: string }) {
  const Link = useSiteLink();
  return (
    <>
      {spans.map((s, i) => {
        const p = `${path}.${i}.text`;
        switch (s.t) {
          case "text":
            return <Editable key={i} as="span" path={p} value={s.text} />;
          case "b":
            return <Editable key={i} as="b" path={p} value={s.text} />;
          case "a":
            return (
              <a key={i} href={s.href} target="_blank" rel="noopener">
                <Editable as="span" path={p} value={s.text} />
              </a>
            );
          case "link":
            return (
              <Link key={i} to={s.to}>
                <Editable as="span" path={p} value={s.text} />
              </Link>
            );
          case "email":
            return <Editable key={i} as="span" path={p} value={s.text} />;
        }
      })}
    </>
  );
}

function Block({ block, path }: { block: LegalBlock; path: string }) {
  switch (block.kind) {
    case "p":
      return (
        <p>
          <Inlines spans={block.spans} path={`${path}.spans`} />
        </p>
      );
    case "h2":
      return (
        <h2 {...(block.id ? { id: block.id } : {})}>
          <Editable as="span" path={`${path}.text`} value={block.text} />
        </h2>
      );
    case "ul":
      return (
        <ul>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inlines spans={item} path={`${path}.items.${i}`} />
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol style={OL_STYLE}>
          {block.items.map((item, i) => (
            <li key={i}>
              <Inlines spans={item} path={`${path}.items.${i}`} />
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {block.columns.map((col, i) => (
                  <th scope="col" key={i}>
                    <Editable as="span" path={`${path}.columns.${i}`} value={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>
                      <Editable as="span" path={`${path}.rows.${i}.${j}`} value={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export function LegalPage({ content }: { content: LegalContent }) {
  const c = content;
  return (
    <>
      <section className="hero page-hero">
        <div className="wrap">
          <div className="hero-copy">
            <Editable as="p" className="eyebrow" path="hero.eyebrow" value={c.hero.eyebrow} />
            <Editable as="h1" path="hero.heading" value={c.hero.heading} />
            <Editable as="p" className="muted" path="hero.dateLabel" value={c.hero.dateLabel} />
          </div>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap prose">
          {c.body.map((block, i) => (
            <Block block={block} path={`body.${i}`} key={i} />
          ))}
        </div>
      </section>
    </>
  );
}
